// Phase 0085 — Task Calendar. Shows tasks for the current user
// across a date range, grouped by date.

import { useState } from 'react'
import { App, Button, Card, DatePicker, Empty, List, Space, Tag, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useQuery } from '@tanstack/react-query'

import { taskApi, type TaskInstance, type TaskStatus } from '@/features/hr/taskExecutionApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const STATUS_COLORS: Record<TaskStatus, string> = {
  PENDING: 'default', IN_PROGRESS: 'blue', COMPLETED: 'cyan',
  VERIFIED: 'green', REJECTED: 'red', OVERDUE: 'volcano',
  BLOCKED: 'orange', SKIPPED: 'default', CANCELLED: 'default',
}

export default function TaskCalendarPage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs().add(7, 'day'),
  ])

  const [from, to] = range
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['task-calendar', from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')],
    queryFn: () => taskApi.calendar(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')),
  })

  // Group by date
  const byDate: Record<string, TaskInstance[]> = {}
  for (const t of tasks) {
    (byDate[t.scheduled_for_date] = byDate[t.scheduled_for_date] || []).push(t)
  }
  const dates = Object.keys(byDate).sort()

  return (
    <>
      <PageHeader
        title="My Task Calendar"
        subtitle="Tasks across a date range, grouped by day"
        extra={
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
          />
        }
      />
      {isLoading ? (
        <Text>Loading…</Text>
      ) : dates.length === 0 ? (
        <Empty description="No tasks in this range" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {dates.map((d) => (
            <Card key={d} title={
              <Space>
                <Text strong>{dayjs(d).format('ddd, MMM D YYYY')}</Text>
                <Tag>{byDate[d].length} tasks</Tag>
              </Space>
            } size="small">
              <List
                size="small"
                dataSource={byDate[d]}
                renderItem={(t) => (
                  <List.Item>
                    <Space>
                      <Tag color={STATUS_COLORS[t.status]}>{t.status}</Tag>
                      {t.priority && <Tag>{t.priority}</Tag>}
                      <Text>{t.title}</Text>
                      {t.due_at && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          due {dayjs(t.due_at).format('HH:mm')}
                        </Text>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          ))}
        </Space>
      )}
    </>
  )
}
